import { user } from '@ohif/core';

export const validateEmail = email => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};
export const isUserLogedIn = () => {
  return Boolean(user.getAccessToken());
};

const parseJwt = token => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export const getEmail = () => {
  return sessionStorage.getItem('email') || localStorage.getItem('email');
};

export const getLoggedInEmail = () => {
  return sessionStorage.getItem('loggedinEmail') || localStorage.getItem('loggedinEmail');
};

export const getToken = () => {
  return sessionStorage.getItem('token') || localStorage.getItem('token');
};

export const getRefreshToken = () => {
  return sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');
};

/**
 * Persist a refreshed access token (and rotated refresh token) into whichever
 * storage currently holds the session — localStorage when "Remember Me" was on,
 * otherwise sessionStorage — so the Remember-Me split is preserved on refresh.
 */
export const storeTokens = (accessToken: string, refreshToken?: string) => {
  const store = localStorage.getItem('token') !== null ? localStorage : sessionStorage;
  if (accessToken) {
    store.setItem('token', accessToken);
  }
  if (refreshToken) {
    store.setItem('refreshToken', refreshToken);
  }
};

/** Expiry (ms epoch) of the current access token's JWT `exp`, or null if absent/malformed. */
export const getAccessTokenExpiryMs = (): number | null => {
  const token = getToken();
  if (!token) {
    return null;
  }
  const decoded = parseJwt(token);
  return decoded?.exp ? decoded.exp * 1000 : null;
};

export const getRole = () => {
  return sessionStorage.getItem('role') || localStorage.getItem('role');
};

export const getHospitalId = () => {
  return sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
};

export const getHospitalName = () => {
  return sessionStorage.getItem('hospitalName') || localStorage.getItem('hospitalName');
};

export const getUserId = (): string | null => {
  return sessionStorage.getItem('userId') || localStorage.getItem('userId');
};

export const isValidUser = () => {
  const userToken = sessionStorage.getItem('token') || localStorage.getItem('token');

  if (!userToken) {
    return false;
  }

  const decodedJwt = parseJwt(userToken);

  // parseJwt returns null for malformed tokens — treat as invalid instead of
  // crashing on `decodedJwt.exp` access (this was causing white-screen lockouts
  // when a corrupt token was in storage).
  if (!decodedJwt || !decodedJwt.exp) {
    return false;
  }

  if (decodedJwt.exp * 1000 < Date.now()) {
    return false;
  }

  return true;
};

export const getPacsToken = () => {
  return sessionStorage.getItem('pacs_access_token') || localStorage.getItem('pacs_access_token');
};

/**
 * Persist a refreshed PACS/DCM (Keycloak `dcm4che` realm) token into whichever
 * storage currently holds the session — localStorage when "Remember Me" was on,
 * otherwise sessionStorage — mirroring storeTokens() so the Remember-Me split is
 * preserved when the DCM token is silently rotated in the background.
 */
export const storePacsToken = (_pacsToken?: string) => {
  // The browser no longer holds a PACS token. All DICOMweb now goes through our backend
  // proxy, authenticated by the app login token, with per-clinic scope enforced
  // server-side. Keeping a privileged PACS token in the browser would let it bypass the
  // proxy by calling the archive directly, so we never store it — and we actively clear
  // any token left over from an older session.
  try {
    sessionStorage.removeItem('pacs_access_token');
    localStorage.removeItem('pacs_access_token');
  } catch {
    /* storage unavailable */
  }
};

/**
 * Expiry (ms epoch) of the current PACS/DCM token's JWT `exp`, or null if
 * absent/malformed. Distinct from getAccessTokenExpiryMs() which reads the app
 * token — the DCM token is a separate Keycloak token with its own, usually
 * shorter, TTL and must be kept fresh independently.
 */
export const getPacsTokenExpiryMs = (): number | null => {
  const token = getPacsToken();
  if (!token) {
    return null;
  }
  const decoded = parseJwt(token);
  return decoded?.exp ? decoded.exp * 1000 : null;
};

export const getName = () => {
  return sessionStorage.getItem('name') || localStorage.getItem('name');
};

/**
 * Keys that the app writes to localStorage / sessionStorage.
 * Only these are removed on logout — we no longer call .clear() which would
 * wipe unrelated data (e.g. third-party or browser-level keys).
 */
const APP_STORAGE_KEYS = [
  'token',
  'refreshToken',
  'role',
  'name',
  'email',
  'loggedinEmail',
  'userId',
  'hospitalId',
  'hospitalName',
  'type',
  'pacsDetails',
  'pacs_access_token',
  'pacs_override',
  // Per-clinic feature flags cached for synchronous route guards. Must be cleared
  // on logout or the next user on this browser inherits the previous clinic's
  // entitlements until their own fetch resolves.
  'clinicFeatures',
] as const;

/**
 * Clears all app-specific keys from both localStorage and sessionStorage,
 * and resets the entire Redux store so no data from the previous session
 * leaks into the next one.
 *
 * A real logout calls logoutAndWipeBrowser, which runs this and then wipes the
 * browser. Call this directly only where the user logs straight back in
 * (switching clinic, re-entering a password).
 *
 * @param dispatch - Redux dispatch (from useDispatch or store.dispatch)
 */
export const clearSessionAndStore = (
  dispatch: (action: any) => void,
  { pushTeardown = true }: { pushTeardown?: boolean } = {}
) => {
  // 0. Detach this browser from the current login's FCM pushes (fire-and-forget).
  //    The auth token MUST be captured synchronously here — the dynamic import
  //    resolves after storage is cleared below. Dynamic import avoids the cycle
  //    firebaseMessaging → authUtils.
  if (pushTeardown) {
    const authTokenForTeardown = getToken();
    import('../firebaseMessaging')
      .then(m => m.teardownWebPush(authTokenForTeardown))
      .catch(() => undefined);
  }

  // 1. Clear app-specific storage keys (both storages, covers Remember Me on/off)
  for (const key of APP_STORAGE_KEYS) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }

  // 2. Reset the entire Redux store (all 60+ slices) in one shot.
  //    The RESET_STORE action is intercepted by the root reducer in store.ts
  //    which passes undefined state to combineReducers, causing every slice
  //    to return its initialState.
  //    We use the raw action type string here instead of importing RESET_STORE
  //    from store.ts to avoid a circular dependency (store → slices → rtk-apis → authUtils → store).
  dispatch({ type: 'RESET_STORE' });

  // 3. Clean up DOM side-effects
  document.body.classList.remove('sidebar-hidden');
};

// Survive a logout wipe: the MFA trusted-device token (else MFA on every login) and
// the local-PACS cookie (saving PACS settings logs out on purpose right after writing it).
const WIPE_KEEP_LOCAL_KEYS = ['trustedDeviceToken'];
const WIPE_KEEP_COOKIES = ['pacs_override'];
const WIPE_STEP_TIMEOUT_MS = 3000;

const withTimeout = (work: Promise<unknown>) =>
  Promise.race([
    work.catch(() => undefined),
    new Promise(resolve => setTimeout(resolve, WIPE_STEP_TIMEOUT_MS)),
  ]);

const expireCookie = (name: string) => {
  const host = window.location.hostname;
  const parts = host.split('.');
  // A cookie can only be removed with the domain it was set on, so try each level.
  const domains = [''];
  for (let i = 0; i < parts.length - 1; i++) {
    domains.push(`; domain=${parts.slice(i).join('.')}`);
  }
  for (const domain of domains) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
  }
};

const deleteDatabase = (name: string) =>
  new Promise<void>(resolve => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });

/** Removes everything this site keeps in the browser except the WIPE_KEEP_* entries. */
const wipeBrowserData = async () => {
  try {
    const kept = WIPE_KEEP_LOCAL_KEYS.map(key => [key, localStorage.getItem(key)] as const);
    localStorage.clear();
    sessionStorage.clear();
    kept.forEach(([key, value]) => value !== null && localStorage.setItem(key, value));
  } catch (e) {}

  try {
    document.cookie
      .split(';')
      .map(c => c.split('=')[0].trim())
      .filter(name => name && !WIPE_KEEP_COOKIES.includes(name))
      .forEach(expireCookie);
  } catch (e) {}

  if (typeof caches !== 'undefined') {
    await withTimeout(caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k)))));
  }
  if (typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function') {
    await withTimeout(
      indexedDB
        .databases()
        .then(dbs => Promise.all(dbs.filter(db => db.name).map(db => deleteDatabase(db.name!))))
    );
  }
  if ('serviceWorker' in navigator) {
    await withTimeout(
      navigator.serviceWorker
        .getRegistrations()
        .then(regs => Promise.all(regs.map(reg => reg.unregister())))
    );
  }
};

/**
 * Full logout: clears the session, wipes this site's browser data (storage, cookies,
 * Cache Storage, IndexedDB, service workers) and hard-loads /login so no old build or
 * state survives. Switching clinic or re-entering a password should keep using
 * clearSessionAndStore, since those log straight back in.
 */
export const logoutAndWipeBrowser = async (dispatch: (action: any) => void) => {
  // Push teardown needs the SW registration and Firebase's IndexedDB, so it runs first.
  const authToken = getToken();
  await withTimeout(import('../firebaseMessaging').then(m => m.teardownWebPush(authToken)));
  clearSessionAndStore(dispatch, { pushTeardown: false });
  await wipeBrowserData();
  window.location.replace('/login');
};
